import type { IncidentOmpAssetContext } from '@/app/services/incident/incident.service';

export type IncidentOwnerTag = {
  kind: 'landlord' | 'operator';
  label: string;
  tooltip?: string;
};

export function resolveAssetOwnerTag(
  asset: IncidentOmpAssetContext,
): IncidentOwnerTag {
  if (!asset.landlordOrgId) {
    return { kind: 'operator', label: 'Operator' };
  }

  return {
    kind: 'landlord',
    label: asset.landlordName?.trim() || asset.landlordOrgId.slice(0, 8),
  };
}

export function hasMixedIncidentOwnership(
  assets: IncidentOmpAssetContext[] | undefined,
): boolean {
  if (!assets?.length) {
    return false;
  }

  const ownershipKeys = new Set(
    assets.map((asset) => asset.landlordOrgId ?? '__operator__'),
  );

  return ownershipKeys.size > 1;
}

export function resolveIncidentOwnerTags(
  assets: IncidentOmpAssetContext[] | undefined,
): IncidentOwnerTag[] {
  if (!assets?.length) {
    return [];
  }

  const landlordsById = new Map<string, string>();
  let hasOperatorOwnedAssets = false;

  for (const asset of assets) {
    if (!asset.landlordOrgId) {
      hasOperatorOwnedAssets = true;
      continue;
    }

    if (!landlordsById.has(asset.landlordOrgId)) {
      landlordsById.set(
        asset.landlordOrgId,
        asset.landlordName?.trim() || asset.landlordOrgId.slice(0, 8),
      );
    }
  }

  const tags: IncidentOwnerTag[] = [...landlordsById.values()].map((name) => ({
    kind: 'landlord',
    label: name,
  }));

  if (hasOperatorOwnedAssets) {
    tags.push({ kind: 'operator', label: 'Operator' });
  }

  return tags;
}
